// The tools picker: attach an operation as a tool — one of the platform's read operations (by its
// area) or an operation of one of the project's app APIs. The tool's name is derived from the
// operationId and can be changed; its description defaults to the operation's summary.
import { Plus } from 'lucide-react';
import * as React from 'react';
import { paramsOf, TOOL_NAME_RE, toolNameOf, type ToolCatalog, type ToolOperation } from './model.ts';
import { btnGhost, input } from './ui.tsx';

export interface DraftTool {
  uid: string;
  name: string;
  /** `sdk`: a tool of an SDK's pack (`app` the SDK, `operationId` the tool: `<sdk>:<tool>`). */
  source: 'operation' | 'app' | 'sdk';
  operationId: string;
  app: string | null;
  description: string;
  operation: ToolOperation | null;
  problem: string | null;
  isNew: boolean;
  removed: boolean;
}

const idOf = (o: ToolOperation) => `${o.source}:${o.app ?? ''}:${o.operationId}`;

export function ToolsPicker({
  catalog,
  tools,
  onAdd,
}: {
  catalog: ToolCatalog | null;
  tools: DraftTool[];
  onAdd: (t: DraftTool) => void;
}) {
  const [pick, setPick] = React.useState('');
  const [name, setName] = React.useState('');
  const [nameTouched, setNameTouched] = React.useState(false);
  const [description, setDescription] = React.useState('');
  const [err, setErr] = React.useState('');

  const all = React.useMemo(
    () => [...(catalog?.operations ?? []), ...(catalog?.apps ?? []).flatMap((a) => a.operations)],
    [catalog],
  );
  const op = all.find((o) => idOf(o) === pick) ?? null;
  const used = new Set(
    tools.filter((t) => !t.removed).map((t) => `${t.source}:${t.app ?? ''}:${t.operationId}`),
  );
  const taken = new Set(tools.filter((t) => !t.removed).map((t) => t.name));
  const toolName = nameTouched ? name : op ? toolNameOf(op.operationId) : '';

  const byTag = new Map<string, ToolOperation[]>();
  // The platform's reads by area, then each SDK pack's tools (`<sdk>:<tool>`, reads and writes) by SDK.
  for (const o of catalog?.operations ?? []) {
    const k = o.source === 'sdk' ? `SDK · ${o.app}` : `Platform · ${o.tag ?? 'platform'}`;
    byTag.set(k, [...(byTag.get(k) ?? []), o]);
  }

  const add = () => {
    setErr('');
    if (!op) return setErr('Pick an operation.');
    if (!TOOL_NAME_RE.test(toolName)) return setErr('A tool name is lower case letters, digits and _.');
    if (taken.has(toolName)) return setErr(`There is already a tool '${toolName}'.`);
    onAdd({
      uid: `new-${Date.now().toString(36)}-${toolName}`,
      name: toolName,
      source: op.source,
      operationId: op.operationId,
      app: op.app,
      description: description.trim(),
      operation: op,
      problem: null,
      isNew: true,
      removed: false,
    });
    setPick('');
    setName('');
    setNameTouched(false);
    setDescription('');
  };

  return (
    <div
      className="rounded-md border border-dashed border-card-border p-3 mt-3"
      data-testid="agent-tool-add-row"
    >
      <div className="text-xs font-semibold text-page-muted mb-2">Attach an operation as a tool</div>
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-2">
        <select
          className={input}
          data-testid="agent-tool-add-operation"
          value={pick}
          onChange={(e) => {
            setPick(e.target.value);
            setNameTouched(false);
          }}
        >
          <option value="">{catalog ? 'Choose an operation…' : 'Loading operations…'}</option>
          {[...byTag].map(([tag, ops]) => (
            <optgroup key={tag} label={tag}>
              {ops.map((o) => (
                <option key={idOf(o)} value={idOf(o)}>
                  {o.source === 'sdk'
                    ? `${o.app}:${o.operationId}${o.access === 'write' ? ' (writes)' : ''}`
                    : o.operationId}
                  {used.has(idOf(o)) ? ' (already a tool)' : ''} — {o.summary}
                </option>
              ))}
            </optgroup>
          ))}
          {(catalog?.apps ?? []).map((a) => (
            <optgroup key={a.name} label={`App · ${a.name}${a.problem ? ` (${a.problem})` : ''}`}>
              {a.operations.map((o) => (
                <option key={idOf(o)} value={idOf(o)}>
                  {o.method} {o.operationId}
                  {used.has(idOf(o)) ? ' (already a tool)' : ''} — {o.summary}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <input
          className={input + ' font-mono'}
          data-testid="agent-tool-add-name"
          placeholder="tool_name"
          value={toolName}
          onChange={(e) => {
            setNameTouched(true);
            setName(e.target.value);
          }}
        />
      </div>
      <input
        className={input + ' mt-2'}
        data-testid="agent-tool-add-desc"
        placeholder={op ? `What it does (default: ${op.summary})` : 'What it does'}
        value={description}
        maxLength={4000}
        onChange={(e) => setDescription(e.target.value)}
      />
      {op ? (
        <div className="mt-2 flex flex-wrap gap-1 text-[11px]">
          <span className="font-mono text-page-muted">
            {op.method} {op.path}
          </span>
          {paramsOf(op.inputSchema).map((p) => (
            <span key={p.name} className="rounded border border-card-border px-1.5 font-mono">
              {p.name}
              {p.required ? '*' : ''}:<span className="text-page-muted">{p.type}</span>
            </span>
          ))}
          {op.access === 'write' ? <span className="text-amber-700">writes</span> : null}
        </div>
      ) : null}
      <div className="flex items-center gap-2 mt-2">
        <button type="button" className={btnGhost} data-testid="agent-tool-add" disabled={!op} onClick={add}>
          <Plus size={14} /> Add tool
        </button>
        {err ? (
          <span className="text-xs text-red-600" data-testid="agent-tool-add-error">
            {err}
          </span>
        ) : null}
      </div>
    </div>
  );
}
